import React from 'react';
import styled from 'styled-components';
import { useInView } from '../utils/scrollAnimations';

const AnimatedWrapper = styled.div`
  opacity: ${props => props.$visible ? 1 : 0};
  transform: ${props => {
    if (!props.$visible) {
      switch (props.$animation) {
        case 'fadeInUp': return 'translateY(30px)';
        case 'fadeInDown': return 'translateY(-30px)';
        case 'fadeInLeft': return 'translateX(-30px)';
        case 'fadeInRight': return 'translateX(30px)';
        case 'scaleIn': return 'scale(0.95)';
        default: return 'translateY(20px)';
      }
    }
    return 'translate(0) scale(1)';
  }};
  transition: opacity ${props => props.$duration || 0.6}s ease-out,
              transform ${props => props.$duration || 0.6}s ease-out;
  transition-delay: ${props => props.$delay || 0}ms;
`;

/**
 * Scroll-triggered animation component
 * @param {string} animation - Animation type: 'fadeInUp', 'fadeInDown', 'fadeInLeft', 'fadeInRight', 'scaleIn'
 * @param {number} delay - Delay before animation starts (ms)
 * @param {number} duration - Animation duration (seconds)
 * @param {number} threshold - Percentage of element visible to trigger (0-1)
 * @param {ReactNode} children - Content to animate
 */
export const ScrollAnimation = ({ 
  children, 
  animation = 'fadeInUp',
  delay = 0,
  duration = 0.6,
  threshold = 0.1,
  className = ''
}) => {
  const [ref, isVisible] = useInView({ threshold, once: true });

  return (
    <AnimatedWrapper
      ref={ref}
      $visible={isVisible}
      $animation={animation}
      $delay={delay}
      $duration={duration}
      className={className}
    >
      {children}
    </AnimatedWrapper>
  );
};

/**
 * Container for staggered children animations
 * @param {ReactNode} children - Child elements to stagger
 * @param {number} staggerDelay - Delay between each child animation (ms)
 */
export const StaggerContainer = ({ children, staggerDelay = 100 }) => {
  return (
    <>
      {React.Children.map(children, (child, index) => {
        if (!child) return null;
        return (
          <ScrollAnimation delay={index * staggerDelay}>
            {child}
          </ScrollAnimation>
        );
      })}
    </>
  );
};

export default ScrollAnimation;
